import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'

interface DropdownItem {
  label: string
  value: string
  icon?: ReactNode
  danger?: boolean
}

interface DropdownProps {
  trigger: ReactNode
  items: DropdownItem[]
  onSelect: (value: string) => void
  align?: 'left' | 'right'
  className?: string
  /** Optional non-interactive content rendered above the item list, e.g. a profile summary. */
  header?: ReactNode
}

export function Dropdown({ trigger, items, onSelect, align = 'left', className, header }: DropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      <button type="button" onClick={() => setOpen((prev) => !prev)} className="inline-flex">
        {trigger}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97, transition: { duration: 0.12 } }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              // Wider with roomier rows on phones — the desktop sizing gave
              // a ~32px tap target, well under a comfortable one.
              'absolute top-full z-50 mt-1.5 min-w-[220px] overflow-hidden rounded-xl border border-shell-border bg-shell py-1 shadow-pop sm:min-w-[180px]',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            {header && <div className="border-b border-shell-border px-3 py-2.5">{header}</div>}
            {items.map((item) => (
              <button
                key={item.value}
                onClick={() => {
                  onSelect(item.value)
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-base transition-colors hover:bg-shell-border/40 sm:gap-2 sm:px-3 sm:py-2 sm:text-sm',
                  item.danger ? 'text-danger-600' : 'text-shell-text',
                )}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
